import { PRODUCT_TYPE, ProductType } from "@/constants";

export enum Route {
  Dashboard = "/",
  Accounts = "/accounts",
  Payees = "/payees",
  Pay = "/pay",
  Exchange = "/exchange",
  Settings = "/settings",
  Support = "/support",
  Recipient = "/recipient",
  NewRecipient = "/recipient/new",
  AccessDenied = "/access-denied",
  Onboarding = "/onboarding",
  OnboardingStatus = "/onboarding/status",
  OnboardingVerification = "/onboarding/verification",
}

export enum PublicRoute {
  LoggedOut = "/logged-out",
  Register = "/register",
  Welcome = "/welcome",
}

/** The step segment that ends every step-flow URL (`…/<id>/success`). */
export const SUCCESS_SEGMENT = "success";

type QueryValue = string | null | undefined;

/**
 * The one query serializer every builder uses: values go through
 * URLSearchParams, so a value holding `&` or `=` stays one parameter. A
 * `null`/`undefined` value is left out, an empty string is kept as `key=`, and
 * a query that ends up empty adds no `?` at all, so appendSearchParams and
 * addReturnTo always see a clean boundary.
 */
const withQuery = (path: string, query: Record<string, QueryValue>) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined) params.append(key, value);
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
};

/** Encodes one path segment. Builders call it once per id; callers never do. */
const segment = (value: string) => encodeURIComponent(value);

/**
 * Turns a dynamic route param back into the raw value. Next hands useParams()
 * values over percent-encoded (the form it keeps in the router tree), and the
 * builders encode, so a page passes a param through this before handing it
 * to a builder; otherwise the id is encoded twice. Generic so a typed param
 * (`ProductType`) keeps its type: decoding cannot change a value that has no
 * escapes, and the builders re-encode whatever it holds.
 */
export const decodeRouteParam = <T extends string>(value: T) =>
  decodeURIComponent(value) as T;

// Exchange routes
export const getExchangeBuyRoute = (currency: string) =>
  withQuery(Route.Exchange, { buy: currency });

export const getExchangeSellRoute = (currency: string) =>
  withQuery(Route.Exchange, { sell: currency });

export const getExchangeReviewRoute = (pricingRequestId: string) =>
  `${Route.Exchange}/${segment(pricingRequestId)}`;

export const getExchangeSuccessRoute = (pricingRequestId: string) =>
  `${getExchangeReviewRoute(pricingRequestId)}/${SUCCESS_SEGMENT}`;

// Pay routes: /pay/<type>/<beneficiaryId>[/<pricingRequestId>[/success]].
// The quote id sits directly under the beneficiary, the same id-first shape
// as exchange, so the [beneficiaryId] layout reads the step from its own
// segments (.claude/rules/web/step-flows.md).
export const getPayInputRoute = ({
  type,
  beneficiaryId,
}: {
  type: ProductType;
  beneficiaryId: string;
}) => `${Route.Pay}/${segment(type)}/${segment(beneficiaryId)}`;

export const getPayReviewRoute = ({
  beneficiaryId,
  pricingRequestId,
  type,
}: {
  beneficiaryId: string;
  pricingRequestId: string;
  type: ProductType;
}) =>
  `${getPayInputRoute({ type, beneficiaryId })}/${segment(pricingRequestId)}`;

export const getPaySuccessRoute = (ids: {
  beneficiaryId: string;
  pricingRequestId: string;
  type: ProductType;
}) => `${getPayReviewRoute(ids)}/${SUCCESS_SEGMENT}`;

export const getPayCurrenciesRoute = (
  beneficiaryId: string,
  buyCurrency: string,
  sellCurrency: string
) =>
  withQuery(
    getPayInputRoute({ type: PRODUCT_TYPE.CURRENCIES, beneficiaryId }),
    { buy: buyCurrency, sell: sellCurrency }
  );

export const getPayDigitalAssetsRoute = (
  beneficiaryId: string,
  buyCurrency: string,
  sellCurrency: string
) =>
  withQuery(
    getPayInputRoute({ type: PRODUCT_TYPE.DIGITAL_ASSETS, beneficiaryId }),
    { buy: buyCurrency, sell: sellCurrency }
  );

export const getPayRoute = (sellCurrency: string) =>
  withQuery(Route.Pay, { sell: sellCurrency || undefined });

// Recipient routes
export const getNewRecipientRoute = (type: ProductType) =>
  `${Route.NewRecipient}/${segment(type)}`;

export const getNewRecipientDetailsRoute = (type: ProductType) =>
  `${getNewRecipientRoute(type)}/details`;

export const getNewRecipientSuccessRoute = (type: ProductType) =>
  `${getNewRecipientRoute(type)}/${SUCCESS_SEGMENT}`;

export const getRecipientRoute = (type: ProductType, beneficiaryId: string) =>
  `${Route.Recipient}/${segment(type)}/${segment(beneficiaryId)}`;

export const getNewRecipientWithTypeRoute = (destinationType: ProductType) =>
  withQuery(Route.NewRecipient, { type: destinationType });

// Registration routes
export const getRegisterAccountTypeRoute = () =>
  `${PublicRoute.Register}/account-type`;

export const getRegisterCapabilitiesRoute = () =>
  `${PublicRoute.Register}/capabilities`;

export const getRegisterNameRoute = () => `${PublicRoute.Register}/name`;

export const getRegisterEmailRoute = () => `${PublicRoute.Register}/email`;

export const getRegisterMobileRoute = () => `${PublicRoute.Register}/mobile`;

export const getRegisterPasswordRoute = () =>
  `${PublicRoute.Register}/password`;

export const getRegisterVerificationRoute = () =>
  `${PublicRoute.Register}/verification`;

export const getRegisterCompanyDetailsRoute = () =>
  `${PublicRoute.Register}/company-details`;

export const getRegisterProcessingRoute = () =>
  `${PublicRoute.Register}/processing`;

export const getRegisterSuccessRoute = () => `${PublicRoute.Register}/success`;

// Onboarding flow types
export type OnboardingType = "business" | "personal";

// Step identifiers for both flows
export enum OnboardingStep {
  // Business-only steps
  Type = "type",
  CompanyDetails = "company-details",
  CompanyAddress = "company-address",
  PrimaryContact = "primary-contact",
  // Personal-only steps
  ContactDetails = "contact-details",
  HomeAddress = "home-address",
  // Shared steps
  AccountUse = "account-use",
  IncomingPayments = "incoming-payments",
  OutgoingPayments = "outgoing-payments",
  Review = "review",
  Terms = "terms",
  Processing = "processing",
  Success = "success",
}

// Onboarding route helpers
export const getOnboardingRoute = () => Route.Onboarding;

export const getOnboardingStepRoute = (
  type: OnboardingType,
  step: OnboardingStep
) => `${Route.Onboarding}/${type}/${step}`;

export const getOnboardingStatusRoute = () => Route.OnboardingStatus;

// Business flow route helpers
export const getOnboardingBusinessTypeRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.Type);
export const getOnboardingBusinessCompanyDetailsRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.CompanyDetails);
export const getOnboardingBusinessCompanyAddressRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.CompanyAddress);
export const getOnboardingBusinessPrimaryContactRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.PrimaryContact);
export const getOnboardingBusinessContactDetailsRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.ContactDetails);
export const getOnboardingBusinessAccountUseRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.AccountUse);
export const getOnboardingBusinessIncomingPaymentsRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.IncomingPayments);
export const getOnboardingBusinessOutgoingPaymentsRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.OutgoingPayments);
export const getOnboardingBusinessReviewRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.Review);
export const getOnboardingBusinessTermsRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.Terms);
export const getOnboardingBusinessProcessingRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.Processing);
export const getOnboardingBusinessSuccessRoute = () =>
  getOnboardingStepRoute("business", OnboardingStep.Success);

// Personal flow route helpers
export const getOnboardingPersonalContactDetailsRoute = () =>
  getOnboardingStepRoute("personal", OnboardingStep.ContactDetails);
export const getOnboardingPersonalHomeAddressRoute = () =>
  getOnboardingStepRoute("personal", OnboardingStep.HomeAddress);
export const getOnboardingPersonalAccountUseRoute = () =>
  getOnboardingStepRoute("personal", OnboardingStep.AccountUse);
export const getOnboardingPersonalIncomingPaymentsRoute = () =>
  getOnboardingStepRoute("personal", OnboardingStep.IncomingPayments);
export const getOnboardingPersonalOutgoingPaymentsRoute = () =>
  getOnboardingStepRoute("personal", OnboardingStep.OutgoingPayments);
export const getOnboardingPersonalReviewRoute = () =>
  getOnboardingStepRoute("personal", OnboardingStep.Review);
export const getOnboardingPersonalTermsRoute = () =>
  getOnboardingStepRoute("personal", OnboardingStep.Terms);
export const getOnboardingPersonalProcessingRoute = () =>
  getOnboardingStepRoute("personal", OnboardingStep.Processing);
export const getOnboardingPersonalSuccessRoute = () =>
  getOnboardingStepRoute("personal", OnboardingStep.Success);

// Generic helpers that use account type
export const getOnboardingContactDetailsRoute = (type: OnboardingType) =>
  getOnboardingStepRoute(type, OnboardingStep.ContactDetails);
export const getOnboardingAccountUseRoute = (type: OnboardingType) =>
  getOnboardingStepRoute(type, OnboardingStep.AccountUse);
export const getOnboardingIncomingPaymentsRoute = (type: OnboardingType) =>
  getOnboardingStepRoute(type, OnboardingStep.IncomingPayments);
export const getOnboardingOutgoingPaymentsRoute = (type: OnboardingType) =>
  getOnboardingStepRoute(type, OnboardingStep.OutgoingPayments);
export const getOnboardingReviewRoute = (type: OnboardingType) =>
  getOnboardingStepRoute(type, OnboardingStep.Review);
export const getOnboardingTermsRoute = (type: OnboardingType) =>
  getOnboardingStepRoute(type, OnboardingStep.Terms);
export const getOnboardingProcessingRoute = (type: OnboardingType) =>
  getOnboardingStepRoute(type, OnboardingStep.Processing);
export const getOnboardingSuccessRoute = (type: OnboardingType) =>
  getOnboardingStepRoute(type, OnboardingStep.Success);
